import type { MinimumTrapOutput, PayoffOutput } from "./types.js";

// Plain-English result copy for credit-card-payoff. Lives in the rules package
// so every surface (Expo result screen, "my results", PDF report) renders
// identical wording from one source. That matters for REGULATION, not just DRY:
// every string here is INFORMATION, not advice (validation §3, panel lens 2/3).
// We state what the payoff IS as arithmetic — how many months/years and how much
// interest at the user's own rate — with NO "you should pay more", NO
// "consolidate", NO "get a balance transfer", NO product handoff, NO
// unconditional "save $X". The min-trap is framed exactly the ASIC MoneySmart
// way: "paying the minimum, this balance takes N years and $X interest". Drift
// from this into advice/credit-assistance is the NCCP kill-line, so the copy is
// centralised here. Pure string-builders, unit-tested with the engine.

/** Whole-dollar AUD with thousands separators. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** A months count as a human "N years M months" (or "M months" under a year). */
export function formatDuration(months: number): string {
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} year${years === 1 ? "" : "s"}`);
  if (rem > 0) parts.push(`${rem} month${rem === 1 ? "" : "s"}`);
  return parts.length > 0 ? parts.join(" ") : "0 months";
}

/** Annual rate as a percentage string, e.g. "20.99%". */
function ratePct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

/** Headline for a single payoff schedule — pure arithmetic, never a nudge. */
export function payoffHeadline(out: PayoffOutput): string {
  if (!out.clears) {
    return `At ${ratePct(out.annualRate)}, this repayment does not cover the monthly interest, so the ${formatAud(out.balance)} balance never reduces.`;
  }
  return `At ${ratePct(out.annualRate)}, the ${formatAud(out.balance)} balance clears in ${formatDuration(out.monthsToClear as number)}, with ${formatAud(out.totalInterestPaid)} in total interest.`;
}

/**
 * The minimum-repayment "trap" framing — stated as ASIC MoneySmart arithmetic.
 * NEVER "consolidate" / "balance transfer" / "you should". The two paths are
 * contrasted as facts; the decision is left entirely to the reader.
 */
export function minimumTrapBullet(out: MinimumTrapOutput): string {
  const min = out.minimumOnly;
  if (!min.clears) {
    return `Paying only the minimum at ${ratePct(out.annualRate)}, the ${formatAud(out.balance)} balance does not meaningfully reduce — the minimum barely keeps pace with the interest.`;
  }
  const interestLine =
    out.minimumInterestExceedsPrincipal
      ? ` — more interest than the ${formatAud(out.balance)} originally owed`
      : "";
  return `Paying only the minimum at ${ratePct(out.annualRate)}, this ${formatAud(out.balance)} balance takes ${formatDuration(min.monthsToClear as number)} and ${formatAud(min.totalInterestPaid)} in interest${interestLine}.`;
}

/**
 * The fixed-vs-minimum contrast — the dramatic extra cost, as cited arithmetic.
 * Conditional framing only ("paying $Y clears it M sooner"), no "save $X".
 */
export function contrastBullet(out: MinimumTrapOutput): string | null {
  if (out.extraMonths === null || out.extraInterest === null) {
    // Minimum path didn't clear within the cap — qualitative statement only.
    return `Paying a fixed amount each month instead of the minimum clears the balance in a defined time; the minimum-only path does not clear within a realistic horizon at this rate.`;
  }
  return `Paying the fixed amount each month clears the same balance ${formatDuration(out.extraMonths)} sooner and pays ${formatAud(out.extraInterest)} less interest than paying only the minimum.`;
}

/** Source-attribution line, composed from the dated benchmark metadata. */
export function sourceBullet(rateVersion: string, verifiedAt: string): string {
  return `This is an estimate using a monthly-compounding model and the minimum-repayment rule described by ASIC MoneySmart (benchmark set ${rateVersion}, last verified ${verifiedAt}). It is an educational tool, not financial advice — always check your own statement and moneysmart.gov.au.`;
}

/** Mandatory disclaimer (binding, validation §3). */
export const DISCLAIMER = "Educational tool. Not financial advice.";

export function explanationBullets(
  out: MinimumTrapOutput,
  rateVersion: string,
  verifiedAt: string,
): string[] {
  const contrast = contrastBullet(out);
  return [
    minimumTrapBullet(out),
    ...(contrast ? [contrast] : []),
    sourceBullet(rateVersion, verifiedAt),
    DISCLAIMER,
  ];
}
